import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  LabelType,
  NgxSliderModule,
  Options,
} from '@angular-slider/ngx-slider';
import { TranslateFunction } from '@angular-slider/ngx-slider/options';

@Component({
  selector: 'app-ngx-slider',
  standalone: true,
  imports: [NgxSliderModule],
  templateUrl: './ngx-slider.component.html',
  styleUrl: './ngx-slider.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NgxSliderComponent),
      multi: true,
    },
  ],
})
export class NgxSliderComponent implements ControlValueAccessor, OnInit {
  value = 0;
  highValue?: number;
  initialized = false;

  @Input() labelFloor?: string;
  @Input() labelCeil?: string;
  @Input() labelToggle?: string;
  @Input() options: Options = {
    floor: 0, // Minimum value
    ceil: 100, // Maximum value
    step: 1, // Step size
    showTicks: false,
  };

  /**
   * Lifecycle hook that is called after Angular has initialized all data-bound properties of the component.
   * This method checks if labelFloor and labelCeil are defined, and if so, assigns a translate function to the options.
   * Marks the component as initialized upon execution.
   *
   * @return {void} This method does not return any value.
   */
  ngOnInit(): void {
    if (this.labelFloor && this.labelCeil) {
      this.options.translate = this.getTranslateFunction();
    }
    this.initialized = true;
  }

  /**
   * Creates and returns a translation function for handling value and label types.
   *
   * The returned function translates numeric values into corresponding string representations
   * based on the label type (e.g., minimum, maximum, or default labels). Custom labels
   * can be used for specific label types if defined.
   *
   * @return {TranslateFunction} A function that takes a number and a label type as input
   * and outputs a translated string representation based on the label type.
   */
  private getTranslateFunction(): TranslateFunction {
    return (value: number, label: LabelType): string => {
      switch (label) {
        case LabelType.Floor:
          return this.labelFloor || String(value); // Custom label for minimum
        case LabelType.Ceil:
          return this.labelCeil || String(value); // Custom label for maximum
        default:
          return this.labelToggle || String(value); // Default for other labels
      }
    };
  }

  private onChange: (value: any) => void = () => {};
  public onTouched: () => void = () => {};

  public onUserChangeEnd: () => void = () => {
    this.onChange(
      this.highValue
        ? { value: this.value, highValue: this.highValue }
        : this.value
    );
  };

  /**
   * Writes a value to the instance. Accepts either a number or an object containing the value and an optional highValue.
   *
   * @param {number | { value: number, highValue?: number }} value - The value to be written. Can either be a number or an object with value and an optional highValue.
   * @return {void} No return value.
   */
  writeValue(value: number | { value: number; highValue?: number }): void {
    if (value && typeof value === 'object') {
      this.value = value.value;
      this.highValue = value.highValue;
    } else {
      this.value = value;
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onValueChange(value: number): void {
    this.writeValue(
      this.highValue ? { value: this.value, highValue: this.highValue } : value
    );
  }

  onHighValueChange(highValue: number): void {
    this.highValue = highValue;
    this.writeValue({ value: this.value, highValue: this.highValue });
  }
}
